/**
 * Onboarding — three short screens, a starter choice, and an honest microphone explanation.
 *
 * It never calls `getUserMedia`. The microphone page is education only: the actual request
 * happens in the Voice Lab, in response to a button whose label says exactly what it does.
 * An app that asks for a microphone during a tour is an app people deny permission to.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { icon, neonButton } from '../components/controls';
import { completeInstalledOnboarding, installedOnboardingPending, isStandalone } from '../../platform/standalone';
import {
  STARTER_TEMPLATES,
  adoptProject,
  createProjectFromTemplate,
  engine,
  reportFailure,
  settings,
  updateSettings,
  type StarterTemplate,
} from '../../app/state';
import { navigate } from '../../app/router';
import { callout } from '../../app/shell';

interface Page {
  kicker: string;
  title: string;
  body: string;
  symbol: string;
  detail: string;
}

const PAGES: readonly Page[] = [
  {
    kicker: 'Step 1',
    title: 'Tap a pad, hear it instantly',
    body: 'PadLab decodes every sound before you touch it, so a pad plays the moment your finger lands. No loading, no delay between the tap and the sound.',
    symbol: 'square.grid.3x3.fill',
    detail: 'Hold several pads at once. The grid is fully multi-touch.',
  },
  {
    kicker: 'Step 2',
    title: 'Record, twist, keep the original',
    body: 'Record your voice, pitch it, stutter it, run it through the rack. Every edit is a setting, not a rewrite: the original recording is never overwritten and undo works across everything.',
    symbol: 'mouth.fill',
    detail: 'Nothing leaves this device unless you share it yourself.',
  },
  {
    kicker: 'Step 3',
    title: 'Build a beat, then take it with you',
    body: 'Program a pattern, play pads in live over the top, then bounce the mix or the stems. Projects live on this device and open offline.',
    symbol: 'music.note.list',
    detail: 'Export lossless WAV any time, or a whole project backup.',
  },
];

export function onboardingScreen(): HTMLElement {
  /**
   * True when this run is the short installed-app tour rather than the full first run — the
   * user has completed onboarding in a browser tab and has now launched the installed app
   * for the first time.
   */
  const installedTourOnly = settings.peek().onboardingComplete && installedOnboardingPending.peek();
  const index = signal(0);
  const chosen = signal<StarterTemplate>(STARTER_TEMPLATES[1] ?? STARTER_TEMPLATES[0]!);
  const working = signal(false);

  const finish = async (): Promise<void> => {
    if (working.peek()) return;
    working.value = true;
    updateSettings({ onboardingComplete: true, micEducationSeen: true });
    // Only when this really is the installed app. Marking the installed tour done from a
    // browser tab meant the tour was already "seen" the first time PadLab was ever launched
    // from the Home Screen — which is the one launch it exists for.
    if (isStandalone.peek()) completeInstalledOnboarding();
    try {
      // The first gesture of the app is the one that starts the audio context. Doing it
      // here means the very first pad the user touches already makes a sound.
      await engine.unlock();
      const created = await createProjectFromTemplate(chosen.peek());
      await adoptProject(created);
      await navigate({ name: 'board', projectID: created.id }, { replace: true });
    } catch (error) {
      reportFailure('PadLab could not build that starter board', error);
      await navigate({ name: 'home' }, { replace: true });
    } finally {
      working.value = false;
    }
  };

  const pageHost = h('div', { class: 'onboard-page' });
  const stopPage = effect(() => {
    const current = index.value;
    pageHost.replaceChildren();
    if (current < PAGES.length) {
      const page = PAGES[current]!;
      pageHost.append(
        h('div', { class: 'onboard-art' }, icon(page.symbol, { size: 44 })),
        h('p', { class: 'kicker' }, page.kicker),
        h('h1', { class: 'onboard-title' }, page.title),
        h('p', { class: 'onboard-body' }, page.body),
        h('p', { class: 'onboard-detail kicker' }, page.detail),
      );
      return;
    }
    // The installed-app page. Different from the browser-mode tour on purpose: someone who
    // has just installed PadLab has already seen the marketing page and the install guide,
    // and what they have *not* seen is that it now works with no connection, that it can
    // hold the microphone, and where the stop-all control lives.
    if (installedTourOnly && current === 0) {
      pageHost.append(
        h('div', { class: 'onboard-art' }, icon('square.grid.3x3.fill', { size: 44 })),
        h('p', { class: 'kicker' }, 'Installed'),
        h('h1', { class: 'onboard-title' }, 'PadLab is on your Home Screen'),
        h(
          'p',
          { class: 'onboard-body' },
          'It works with no connection now. Every sound is on this device. The microphone is only ever opened from the Voice Lab, when you press the button that asks for it, and released the moment you leave. If anything ever sticks on, the ! button in the transport stops every voice at once.',
        ),
      );
      return;
    }

    if (current === PAGES.length) {
      pageHost.append(
        h('p', { class: 'kicker' }, 'One more thing'),
        h('h1', { class: 'onboard-title' }, 'About the microphone'),
        h(
          'p',
          { class: 'onboard-body' },
          'PadLab records through your microphone only when you press record in the Voice Lab. It is never opened at launch, never opened in the background, and the recording indicator only appears while audio frames are genuinely arriving.',
        ),
        callout(
          'When you are asked',
          'Your browser will ask once',
          'Safari shows its own permission prompt the first time you tap “Enable microphone”. If you say no, PadLab keeps working and you can still import sounds. The Voice Lab shows you where to change your mind in Settings.',
          'accent',
        ),
        h(
          'p',
          { class: 'onboard-detail kicker' },
          'Recordings stay on this device. Nothing is uploaded.',
        ),
      );
      return;
    }
    pageHost.append(
      h('p', { class: 'kicker' }, 'Pick a starting point'),
      h('h1', { class: 'onboard-title' }, 'What do you want to make?'),
      h(
        'div',
        { class: 'onboard-templates' },
        ...STARTER_TEMPLATES.map((template) => {
          const button = h(
            'button',
            {
              class: 'onboard-template',
              type: 'button',
              onclick: () => {
                chosen.value = template;
              },
            },
            icon(template.symbol, { size: 20 }),
            h(
              'span',
              { class: 'onboard-template-text' },
              h('span', { class: 'onboard-template-name' }, template.name),
              h('span', { class: 'onboard-template-summary' }, template.summary),
            ),
            // The chosen row is the only one wearing a checkmark. It replaces the tinted
            // outline this list used to draw round the selection: a glyph is a shape, so it
            // reads in greyscale, at a glance, and in a screenshot.
            h('span', { class: 'onboard-template-check', 'aria-hidden': 'true' }, icon('checkmark', { size: 16 })),
          );
          const stop = effect(() => {
            const on = chosen.value.id === template.id;
            button.classList.toggle('onboard-template--on', on);
            // Same statement, made programmatically — `aria-current` is what the project list
            // already uses for "this is the one".
            if (on) button.setAttribute('aria-current', 'true');
            else button.removeAttribute('aria-current');
          });
          onCleanup(button, stop);
          return button;
        }),
      ),
    );
  });
  onCleanup(pageHost, stopPage);

  const dots = h('div', { class: 'onboard-dots', role: 'presentation' });
  for (let step = 0; step <= PAGES.length + 1; step++) {
    const dot = h('span', { class: 'onboard-dot' });
    const stop = effect(() => dot.classList.toggle('onboard-dot--on', index.value === step));
    onCleanup(dot, stop);
    dots.appendChild(dot);
  }

  const isLast = computed(() => (installedTourOnly ? true : index.value === PAGES.length + 1));
  const primary = neonButton({
    title: computed(() => (isLast.value ? 'Start playing' : 'Continue')),
    style: 'primary',
    fullWidth: true,
    enabled: computed(() => !working.value),
    onPress: () => {
      if (isLast.peek()) void finish();
      else index.value = index.peek() + 1;
    },
  });

  return h(
    'div',
    { class: 'screen screen--onboarding safe-all' },
    h(
      'div',
      { class: 'onboard-top' },
      h(
        'button',
        {
          class: 'onboard-skip',
          type: 'button',
          onclick: () => {
            updateSettings({ onboardingComplete: true });
            if (isStandalone.peek()) completeInstalledOnboarding();
            void navigate({ name: 'home' }, { replace: true });
          },
        },
        'Skip',
      ),
    ),
    pageHost,
    h(
      'div',
      { class: 'onboard-foot' },
      dots,
      primary.el,
      h(
        'button',
        {
          class: 'onboard-back',
          type: 'button',
          onclick: () => {
            index.value = Math.max(0, index.peek() - 1);
          },
        },
        'Back',
      ),
    ),
  );
}
